/**
 * Select Component
 * 
 * A customizable dropdown select component with support for
 * single selection, search, and various states.
 */
import { useEffect, useRef, useState } from 'react'
import { cn, twStyles } from '../../utils/tailwind-utils'

interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

interface SelectProps {
  /**
   * Array of options to display in the dropdown
   */
  options: SelectOption[]
  
  /**
   * Currently selected value
   */
  value?: string
  
  /**
   * Function called when selection changes
   */
  onChange: (value: string) => void
  
  /**
   * Placeholder text when no option is selected
   */
  placeholder?: string
  
  /**
   * Whether the select is disabled
   */
  disabled?: boolean
  
  /**
   * Error message to display
   */
  error?: string
  
  /**
   * Label for the select
   */
  label?: string
  
  /**
   * Helper text to display below the select
   */
  helperText?: string
  
  /**
   * Whether to enable searching/filtering options
   */
  searchable?: boolean
  
  /**
   * Additional CSS classes
   */
  className?: string
  
  /**
   * Additional CSS classes for the wrapper
   */
  wrapperClassName?: string
  
  /**
   * ID for the select element
   */
  id?: string
  
  /**
   * Name for the select element
   */
  name?: string
  
  /**
   * Whether the field is required
   */
  required?: boolean
}

/**
 * Custom select/dropdown component with enhanced functionality
 * 
 * @example
 * // Basic usage
 * <Select 
 *   options={[
 *     { value: 'option1', label: 'Option 1' },
 *     { value: 'option2', label: 'Option 2' }
 *   ]}
 *   value={selectedValue}
 *   onChange={setSelectedValue}
 * />
 * 
 * @example
 * // With label and error
 * <Select 
 *   label="Choose a category"
 *   options={categories}
 *   value={category}
 *   onChange={setCategory}
 *   error="Please select a category"
 * />
 * 
 * @example
 * // Searchable select
 * <Select 
 *   options={users}
 *   value={selectedUser}
 *   onChange={setSelectedUser}
 *   searchable
 *   placeholder="Search users..."
 * />
 */
function Select({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  error,
  label,
  helperText,
  searchable = false,
  className,
  wrapperClassName,
  id,
  name,
  required,
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const selectRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  
  // Find the selected option
  const selectedOption = options.find(option => option.value === value)
  
  // Filter options based on search term
  const filteredOptions = searchable && searchTerm
    ? options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options
  
  // Handle clicking outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  
  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen, searchable])
  
  // Toggle dropdown
  function toggleDropdown() {
    if (!disabled) {
      setIsOpen(!isOpen)
      setSearchTerm('')
    }
  }
  
  // Handle option selection
  function handleSelect(option: SelectOption) {
    if (!option.disabled) {
      onChange(option.value)
      setIsOpen(false)
      setSearchTerm('')
    }
  }
  
  // Handle search input change
  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchTerm(e.target.value)
  }
  
  // Handle keyboard navigation
  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      setIsOpen(false)
      setSearchTerm('')
    }
  }
  
  // Determine if there's an error
  const hasError = !!error
  
  return (
    <div className={cn('space-y-2', wrapperClassName)}>
      {/* Label */}
      {label && (
        <label 
          htmlFor={id} 
          className={cn(
            'block text-sm font-medium',
            hasError ? 'text-destructive' : 'text-foreground'
          )}
        >
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      
      {/* Select container */}
      <div 
        ref={selectRef}
        className={cn(
          'relative',
          className
        )}
      >
        {/* Select trigger */}
        <div
          className={cn(
            twStyles.input.base,
            'flex items-center justify-between cursor-pointer',
            hasError && 'border-destructive focus-visible:ring-destructive',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          onClick={toggleDropdown}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={`select-dropdown-${id || name}`}
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <span className={cn(!selectedOption && 'text-muted-foreground')}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            className={cn(
              'w-4 h-4 transition-transform',
              isOpen && 'transform rotate-180'
            )}
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
        
        {/* Dropdown */}
        {isOpen && (
          <div 
            className="absolute z-10 w-full mt-1 bg-card border border-border rounded-md shadow-md"
            id={`select-dropdown-${id || name}`}
            role="listbox"
          >
            {/* Search input */}
            {searchable && (
              <div className="p-2 border-b border-border">
                <input
                  ref={inputRef}
                  type="text"
                  className={cn(twStyles.input.base, 'h-8')}
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                />
              </div>
            )}
            
            {/* Options list */}
            <div className="max-h-60 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map(option => (
                  <div
                    key={option.value}
                    className={cn(
                      'px-3 py-2 cursor-pointer',
                      option.value === value ? 'bg-primary/10 text-primary' : 'hover:bg-muted',
                      option.disabled && 'opacity-50 cursor-not-allowed'
                    )}
                    onClick={() => handleSelect(option)}
                    role="option"
                    aria-selected={option.value === value}
                  >
                    {option.label}
                  </div>
                ))
              ) : (
                <div className="px-3 py-2 text-muted-foreground">
                  No options found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Error message or helper text */}
      {(error || helperText) && (
        <p 
          className={cn(
            'text-sm',
            hasError ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {error || helperText}
        </p>
      )}
    </div>
  )
}

export default Select
